'use client'

import { Suspense } from 'react'
import dynamic from 'next/dynamic'

// Define proper types for the component props
interface AddToCartButtonProps {
  productId: string;
  moqGrams: number;
  addToCartText: string;
  quantityLabel: string;
  defaultUnit?: string;
  unitOptions?: Array<{value: string, label: string}>;
  defaultQuantity?: number;
  conversionFactor?: number;
  productOptions?: Array<{name: string, extraPrice: number}>;
  kg_price?: boolean;
}

interface ImageGalleryProps {
  mainImage: string;
  additionalImages: string[];
}

// Import the actual type from TabsClient to ensure compatibility
const TabsClient = dynamic(() => import('./TabsClient'), {
  loading: () => <div className="h-64 w-full bg-surface animate-pulse rounded-lg"></div>,
  ssr: false
});

// Dynamically import client components with lazy loading
const AddToCartButtonComponent = dynamic(() => import('./AddToCartButton'), {
  loading: () => <div className="h-12 w-full bg-surface animate-pulse rounded-lg"></div>,
  ssr: false
});

const ImageGalleryClientComponent = dynamic(() => import('./ImageGalleryClient'), {
  loading: () => (
    <div className="w-full h-[400px] md:h-[500px] lg:h-[600px] bg-surface-hover rounded-lg flex items-center justify-center">
      <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
    </div>
  ),
  ssr: false
});

// Wrapper components that can be imported in server components
export function AddToCartButtonWrapper(props: AddToCartButtonProps) {
  return (
    <Suspense fallback={<div className="h-12 w-full bg-surface animate-pulse rounded-lg"></div>}>
      <AddToCartButtonComponent {...props} />
    </Suspense>
  )
}

export function ImageGalleryWrapper(props: ImageGalleryProps) {
  return (
    <Suspense fallback={
      <div className="w-full h-[400px] md:h-[500px] lg:h-[600px] bg-surface-hover rounded-lg flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    }>
      <ImageGalleryClientComponent {...props} />
    </Suspense>
  )
}

// For TabsWrapper, we use a more generic type definition that's compatible with TabsClient
export function TabsWrapper(props: {
  tabData: Record<string, {
    title: string;
    content?: string | Array<{name: string, value: string}> | Record<string, string | number | boolean | null> | null;
    articleSlug?: string;
    readMore?: string;
    labels?: {
      batchNumber: string;
      testDate: string;
      expirationDate: string;
      downloadCoa: string;
      na: string;
    };
  }>;
  locale: string;
  formatSpecKey?: (key: string) => string;
}) {
  return (
    <Suspense fallback={<div className="h-64 w-full bg-surface animate-pulse rounded-lg"></div>}>
      <TabsClient {...props} />
    </Suspense>
  )
} 